import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { LogIn, Eye, EyeOff } from 'lucide-react';

export default function LoginPage() {
  const { t, lang } = useLanguage();
  const { login, formatApiError } = useAuth();
  const navigate = useNavigate();
  const displayFont = lang === 'ar' ? 'var(--font-ar-display)' : 'var(--font-en-display)';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(email, password);
      navigate(user?.role === 'admin' ? '/admin' : '/portal');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen bg-gradient-to-br from-[#0B2F44] to-[#071E2C] flex items-center justify-center px-4 pt-20">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl p-8 shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-14 h-14 bg-[#0B2F44] rounded-xl flex items-center justify-center mx-auto mb-4">
              <LogIn className="w-7 h-7 text-[#C9A84C]" />
            </div>
            <h1 className="text-2xl font-bold text-[#0B2F44]" style={{ fontFamily: displayFont }}>
              {t('nav', 'login')}
            </h1>
            <p className="text-sm text-[#4A6572] mt-1">
              {lang === 'ar' ? 'سجل دخولك إلى بوابة العميل' : 'Sign in to your client portal'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-sm text-[#4A6572] block mb-1.5">{t('contact', 'email')}</label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="h-[52px] border-[#D0D8DC] focus:border-[#A68966]"
                required
              />
            </div>
            <div>
              <label className="text-sm text-[#4A6572] block mb-1.5">{lang === 'ar' ? 'كلمة المرور' : 'Password'}</label>
              <div className="relative">
                <Input
                  data-testid="login-password-input"
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="h-[52px] border-[#D0D8DC] focus:border-[#A68966] pe-12"
                  required
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute end-3 top-1/2 -translate-y-1/2 text-[#4A6572]">
                  {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {error && <p data-testid="login-error" className="text-[#C0392B] text-sm bg-[#C0392B]/5 p-3 rounded-lg">{error}</p>}

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full bg-[#C9A84C] hover:bg-[#A68966] text-[#0B2F44] font-bold py-6"
            >
              {loading ? (lang === 'ar' ? 'جاري الدخول...' : 'Signing in...') : t('nav', 'login')}
            </Button>
          </form>

          <p className="text-center text-sm text-[#4A6572] mt-6">
            {lang === 'ar' ? 'ليس لديك حساب؟' : "Don't have an account?"}{' '}
            <Link to="/register" className="text-[#A68966] font-semibold hover:underline">{t('nav', 'register')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
